import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ApiConfiguration } from '@/core/api/api-configuration';
import { BaseService } from '@/core/api/base-service';
import {
    ComissaoConselheiroAtuacaoPageResponse,
    ComissaoEstatisticaAgrupadaResponse,
    ComissaoEstatisticaAnaliticoPageResponse,
    ComissaoEstatisticaAtividadeAnaliticoFiltroRequest,
    ComissaoEstatisticaAtividadeFiltroRequest,
    ComissaoEstatisticaConfirmacaoResponse,
    ComissaoEstatisticaFunilResponse,
    ComissaoEstatisticaReuniaoAnaliticoFiltroRequest,
    ComissaoEstatisticaReuniaoFiltroRequest,
    ComissaoReuniaoVariacaoMediaResponse
} from '@/core/api/sicom-api/comissoes/estatisticas/comissao-estatistica.model';
import { environment } from '@env/environment';

import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ComissaoEstatisticaService extends BaseService {
    readonly PATH = environment.basePath + '/comissoes/estatisticas';

    constructor(config: ApiConfiguration, http: HttpClient) {
        super(config, http, environment.apis.apiSicom);
    }

    listarConselheirosAtuantes(request: ComissaoEstatisticaReuniaoFiltroRequest, escopo?: string, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        const params = escopo ? new HttpParams().set('escopo', escopo) : undefined;
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/conselheiros/atuantes`, request, { params, context });
    }

    listarConselheirosFaltas(request: ComissaoEstatisticaReuniaoFiltroRequest, escopo?: string, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        const params = escopo ? new HttpParams().set('escopo', escopo) : undefined;
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/conselheiros/faltas`, request, { params, context });
    }

    consultarAtuacaoConselheiros(request: ComissaoEstatisticaReuniaoFiltroRequest, page = 0, size = 20, sort?: string, escopo?: string, context?: HttpContext): Observable<ComissaoConselheiroAtuacaoPageResponse> {
        let params = new HttpParams().set('page', page).set('size', size);
        if (sort) {
            params = params.set('sort', sort);
        }
        if (escopo) {
            params = params.set('escopo', escopo);
        }
        return this.http.post<ComissaoConselheiroAtuacaoPageResponse>(`${this.PATH}/analitico/conselheiros/atuacao`, request, { params, context });
    }

    consultarVariacaoMediaReuniao(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoReuniaoVariacaoMediaResponse> {
        return this.http.post<ComissaoReuniaoVariacaoMediaResponse>(`${this.PATH}/reunioes/variacao-media`, request, { context });
    }

    consultarAnaliticoReunioesConselheiro(idSolicitante: number, ufSgUf: string, request: ComissaoEstatisticaReuniaoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/conselheiros/${idSolicitante}/${ufSgUf}/reunioes`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoComissoesConselheiro(idSolicitante: number, ufSgUf: string, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(
            `${this.PATH}/analitico/conselheiros/${idSolicitante}/${ufSgUf}/comissoes`,
            {},
            {
                params: this.criarPageParams(page, size),
                context
            }
        );
    }

    listarReunioesPorStatus(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/reunioes/status`, request, { context });
    }

    listarReunioesEvolucaoMensal(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/reunioes/evolucao-mensal`, request, { context });
    }

    listarReunioesPorTipoComissao(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/reunioes/tipo-comissao`, request, { context });
    }

    listarReunioesPorModalidade(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/reunioes/modalidade`, request, { context });
    }

    listarAtosPorComissao(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/reunioes/atos-comissao`, request, { context });
    }

    listarAtosDistribuicao(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/reunioes/atos-distribuicao`, request, { context });
    }

    listarReunioesPorAssistente(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/reunioes/assistente`, request, { context });
    }

    listarReunioesPorCoordenador(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/reunioes/coordenador`, request, { context });
    }

    listarReunioesPorSecretario(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/reunioes/secretario`, request, { context });
    }

    listarFunilOperacional(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaFunilResponse[]> {
        return this.http.post<ComissaoEstatisticaFunilResponse[]>(`${this.PATH}/reunioes/funil-operacional`, request, { context });
    }

    listarConfirmacoesPorReuniao(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaConfirmacaoResponse[]> {
        return this.http.post<ComissaoEstatisticaConfirmacaoResponse[]>(`${this.PATH}/reunioes/confirmacoes`, request, { context });
    }

    listarReunioesPorLocal(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/reunioes/local`, request, { context });
    }

    listarQuorumMedioComissao(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/reunioes/quorum-medio-comissao`, request, { context });
    }

    listarQuorumFaixa(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/reunioes/quorum-faixa`, request, { context });
    }

    listarReunioesDuracaoFaixa(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/reunioes/duracao-faixa`, request, { context });
    }

    listarReunioesDuracaoModalidade(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/reunioes/duracao-modalidade`, request, { context });
    }

    listarAtasLeadTimeFaixa(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/atas/lead-time-faixa`, request, { context });
    }

    listarAtasLeadTimeComissao(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/atas/lead-time-comissao`, request, { context });
    }

    listarReunioesLocalInternoExterno(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/reunioes/local-interno-externo`, request, { context });
    }

    listarReunioesPorVisibilidadePortal(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/reunioes/visibilidade-portal`, request, { context });
    }

    listarAtasPorImportancia(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/atas/importancia`, request, { context });
    }

    listarReunioesAgendaSemanal(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/reunioes/agenda-semanal`, request, { context });
    }

    listarAtasProducaoMensal(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/atas/producao-mensal`, request, { context });
    }

    listarAtividadesEvolucaoMensal(request: ComissaoEstatisticaAtividadeFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/atividades/evolucao-mensal`, request, { context });
    }

    listarAtasPorStatus(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/atas/status`, request, { context });
    }

    listarAtasPorProtocolo(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/atas/protocolo`, request, { context });
    }

    listarPendenciasConvocacao(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/convocacoes/pendencias`, request, { context });
    }

    listarConvocacaoPresencas(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/convocacoes/presencas`, request, { context });
    }

    listarConvocacaoEmails(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/convocacoes/emails`, request, { context });
    }

    listarConvocacoesPassagensMes(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/convocacoes/passagens-mes`, request, { context });
    }

    listarConvocacoesMeio(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/convocacoes/meio`, request, { context });
    }

    listarConvocacoesSmsStatus(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/convocacoes/sms-status`, request, { context });
    }

    listarConvocacoesEmailSms(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/convocacoes/email-sms`, request, { context });
    }

    listarConvocacoesConvidados(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/convocacoes/convidados`, request, { context });
    }

    listarConvocacoesReunioesConvidados(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/convocacoes/reunioes-convidados`, request, { context });
    }

    listarComissoesPorTipo(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/comissoes/tipo`, request, { context });
    }

    listarComissoesPorStatus(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/comissoes/status`, request, { context });
    }

    listarComissoesSemReuniaoFutura(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/comissoes/sem-reuniao-futura`, request, { context });
    }

    listarComissoesExtintasAno(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/comissoes/extintas-por-ano`, request, { context });
    }

    listarComissoesVidaMediaTipo(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/comissoes/vida-media-tipo`, request, { context });
    }

    listarComissoesEntidadesExternas(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/comissoes/entidades-externas`, request, { context });
    }

    listarComissoesComSemNucleos(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/comissoes/com-sem-nucleos`, request, { context });
    }

    listarComissoesNucleosSituacao(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/comissoes/nucleos-situacao`, request, { context });
    }

    listarComissoesFaixaMembros(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/comissoes/faixa-membros`, request, { context });
    }

    listarComissoesCoordenador(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/comissoes/coordenador`, request, { context });
    }

    listarComissoesAntiguidade(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/comissoes/antiguidade`, request, { context });
    }

    listarMembrosPorParticipacao(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/membros/participacao`, request, { context });
    }

    listarMembrosPorStatus(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/membros/status`, request, { context });
    }

    listarMembrosPorUf(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/membros/uf`, request, { context });
    }

    listarMembrosMultivinculo(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/membros/multivinculo`, request, { context });
    }

    listarMembrosEntradasMes(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/membros/entradas-mes`, request, { context });
    }

    listarMembrosMandatoTipo(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/membros/mandato-tipo`, request, { context });
    }

    listarMembrosStatusHistorico(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/membros/status-historico`, request, { context });
    }

    listarMembrosLgpd(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/membros/lgpd`, request, { context });
    }

    listarMembrosQuitacao(request: ComissaoEstatisticaReuniaoFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/membros/quitacao`, request, { context });
    }

    listarAtividadesPorSituacao(request: ComissaoEstatisticaAtividadeFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/atividades/situacao`, request, { context });
    }

    listarAtividadesPorResponsavel(request: ComissaoEstatisticaAtividadeFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/atividades/responsavel`, request, { context });
    }

    listarAtividadesPorPrazo(request: ComissaoEstatisticaAtividadeFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/atividades/prazo`, request, { context });
    }

    listarAtividadesPorComissao(request: ComissaoEstatisticaAtividadeFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/atividades/comissao`, request, { context });
    }

    listarAtividadesPorTempoConclusao(request: ComissaoEstatisticaAtividadeFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/atividades/tempo-conclusao`, request, { context });
    }

    listarAtividadesPorOrigem(request: ComissaoEstatisticaAtividadeFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/atividades/origem`, request, { context });
    }

    listarAtividadesPorProtocolo(request: ComissaoEstatisticaAtividadeFiltroRequest, context?: HttpContext): Observable<ComissaoEstatisticaAgrupadaResponse[]> {
        return this.http.post<ComissaoEstatisticaAgrupadaResponse[]>(`${this.PATH}/atividades/protocolo`, request, { context });
    }

    consultarAnaliticoReunioesPorStatus(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/reunioes/status`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoReunioesEvolucaoMensal(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/reunioes/evolucao-mensal`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoReunioesPorTipoComissao(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/reunioes/tipo-comissao`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoReunioesPorModalidade(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/reunioes/modalidade`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoAtosPorComissao(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/reunioes/atos-comissao`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoAtosDistribuicao(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/reunioes/atos-distribuicao`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoReunioesPorAssistente(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/reunioes/assistente`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoReunioesPorCoordenador(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/reunioes/coordenador`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoReunioesPorSecretario(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/reunioes/secretario`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoFunilOperacional(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/reunioes/funil-operacional`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoConfirmacoesPorReuniao(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/reunioes/confirmacoes`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoReunioesPorLocal(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/reunioes/local`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoQuorumMedioComissao(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/reunioes/quorum-medio-comissao`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoQuorumFaixa(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/reunioes/quorum-faixa`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoReunioesDuracaoFaixa(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/reunioes/duracao-faixa`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoAtasLeadTimeFaixa(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/atas/lead-time-faixa`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoReunioesPorVisibilidadePortal(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/reunioes/visibilidade-portal`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoAtasPorImportancia(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/atas/importancia`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoReunioesAgendaSemanal(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/reunioes/agenda-semanal`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoAtasProducaoMensal(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/atas/producao-mensal`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoAtividadesEvolucaoMensal(request: ComissaoEstatisticaAtividadeAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/atividades/evolucao-mensal`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoAtasPorStatus(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/atas/status`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoAtasPorProtocolo(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/atas/protocolo`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoPendenciasConvocacao(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/convocacoes/pendencias`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoConvocacaoPresencas(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/convocacoes/presencas`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoConvocacaoEmails(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/convocacoes/emails`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoConvocacoesMeio(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/convocacoes/meio`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoConvocacoesSmsStatus(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/convocacoes/sms-status`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoConvocacoesConvidados(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/convocacoes/convidados`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoComissoesPorTipo(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/comissoes/tipo`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoComissoesPorStatus(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/comissoes/status`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoComissoesSemReuniaoFutura(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/comissoes/sem-reuniao-futura`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoComissoesExtintasAno(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/comissoes/extintas-por-ano`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoComissoesEntidadesExternas(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/comissoes/entidades-externas`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoComissoesFaixaMembros(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/comissoes/faixa-membros`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoMembrosPorParticipacao(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/membros/participacao`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoMembrosMultivinculo(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/membros/multivinculo`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoMembrosPorStatus(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/membros/status`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoMembrosPorUf(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/membros/uf`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoMembrosLgpd(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/membros/lgpd`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoMembrosQuitacao(request: ComissaoEstatisticaReuniaoAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/membros/quitacao`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoAtividadesPorSituacao(request: ComissaoEstatisticaAtividadeAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/atividades/situacao`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoAtividadesPorResponsavel(request: ComissaoEstatisticaAtividadeAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/atividades/responsavel`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoAtividadesPorPrazo(request: ComissaoEstatisticaAtividadeAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/atividades/prazo`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoAtividadesPorComissao(request: ComissaoEstatisticaAtividadeAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/atividades/comissao`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    consultarAnaliticoAtividadesPorTempoConclusao(request: ComissaoEstatisticaAtividadeAnaliticoFiltroRequest, page = 0, size = 100, context?: HttpContext): Observable<ComissaoEstatisticaAnaliticoPageResponse> {
        return this.http.post<ComissaoEstatisticaAnaliticoPageResponse>(`${this.PATH}/analitico/atividades/tempo-conclusao`, request, {
            params: this.criarPageParams(page, size),
            context
        });
    }

    private criarPageParams(page: number, size: number): HttpParams {
        return new HttpParams().set('page', String(page)).set('size', String(size));
    }
}
